import { createStore } from 'zustand';
import { devtools } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';
import UserPreferences from '../../../service/userPreferences';
import CloneUtils from '../../../utils/cloneUtils';
import GlobalReportsRepo from '../../../utils/repository/globalRepoertsRepo';
import Alert from '../../../utils/alert';
import { ConfigProps } from '../../../components/financialTables/configuration/constants';
import { getChartableDescriptors } from '../utils';
import { CompanyComparisonData, CompanyWithSchemas, ComparisonReport } from '../types';

// ─── State types ──────────────────────────────────────────────────────────────

export type ComparisonStoreState = {
    companies: CompanyWithSchemas[];
    selectedSchemaTypes: string[];
    schemaData: Record<number, CompanyComparisonData>;
    filterCfg: Record<string, unknown>;
    tableConfig: Record<string, unknown>;
};

// ─── Action types ─────────────────────────────────────────────────────────────

type CompanyActions = {
    addCompany: (company: CompanyWithSchemas) => void;
    removeCompany: (companyId: number) => void;
};

type SchemaActions = {
    setSchemaTypes: (schemaTypes: string[]) => void;
    setCompanyData: (schemaId: number, data: Partial<CompanyComparisonData>) => void;
};

type ConfigActions = {
    setFilterCfg: (cfg: Record<string, unknown>) => void;
    setTableConfig: (cfg: Record<string, unknown>) => void;
};

export type ComparisonStoreActions = {
    companyActions: CompanyActions;
    schemaActions: SchemaActions;
    configActions: ConfigActions;
};

export type ComparisonStoreType = ComparisonStoreState & ComparisonStoreActions;

function getDefaultFilterCfg(): Record<string, unknown> {
    return CloneUtils.deepClone(UserPreferences.get(UserPreferences.FILTER_CFG_PROP));
}

function getDefaultTableConfig(): Record<string, unknown> {
    const base = CloneUtils.deepClone(UserPreferences.get(UserPreferences.TABLE_CFG_PROP));
    return {
        ...base,
        [ConfigProps.SHOW_NOTES]: true,
        [ConfigProps.CATEGORY_BASED_MODE]: true,
        [ConfigProps.SHOW_ORIGINAL_CELL_DATA_IF_LATEST_MISSING]: true,
    };
}

const DEFAULT_STATE: ComparisonStoreState = {
    companies: [],
    selectedSchemaTypes: [],
    schemaData: {},
    filterCfg: getDefaultFilterCfg(),
    tableConfig: getDefaultTableConfig(),
};

export const createComparisonStore = (initialState?: Partial<ComparisonStoreState>) => {
    return createStore<ComparisonStoreType>()(
        devtools(
            immer((set, get) => {
                const loadReports = (schemaId: number) => {
                    const data = get().schemaData[schemaId];
                    if (!data?.schema) {
                        return;
                    }

                    const { schema, company } = data;
                    const descriptors = getChartableDescriptors(schema.descriptors);
                    set((state) => {
                        if (state.schemaData[schemaId]) {
                            state.schemaData[schemaId].descriptors = descriptors;
                        }
                    });

                    GlobalReportsRepo.listBySchema(
                        schema.id,
                        (reports: unknown) => {
                            set((state) => {
                                if (state.schemaData[schemaId]) {
                                    state.schemaData[schemaId].reports = reports as ComparisonReport[];
                                    state.schemaData[schemaId].loading = false;
                                }
                            });
                        },
                        () => {
                            set((state) => {
                                delete state.schemaData[schemaId];
                            });
                            Alert.warn(`Failed to load reports for ${company.name} — ${schema.name}`);
                        },
                    );
                };

                return {
                    ...DEFAULT_STATE,
                    ...initialState,

                    companyActions: {
                        addCompany: (company) => {
                            if (get().companies.some((c) => c.id === company.id)) {
                                return;
                            }

                            const schemaIdsToLoad: number[] = [];
                            set((state) => {
                                state.companies.push(company);
                                for (const schemaType of state.selectedSchemaTypes) {
                                    const schema = company.schemas.find(
                                        (s) => s.name === schemaType && s.branchType === 'MAIN',
                                    );
                                    if (schema) {
                                        state.schemaData[schema.id] = {
                                            company,
                                            schema,
                                            descriptors: [],
                                            reports: [],
                                            loading: true,
                                        };
                                        schemaIdsToLoad.push(schema.id);
                                    }
                                }
                            });

                            for (const schemaId of schemaIdsToLoad) {
                                loadReports(schemaId);
                            }
                        },

                        removeCompany: (companyId) =>
                            set((state) => {
                                state.companies = state.companies.filter((c) => c.id !== companyId);
                                for (const key of Object.keys(state.schemaData)) {
                                    const numKey = Number(key);
                                    if (state.schemaData[numKey].company.id === companyId) {
                                        delete state.schemaData[numKey];
                                    }
                                }
                            }),
                    },

                    schemaActions: {
                        setSchemaTypes: (schemaTypes) => {
                            const prevTypes = new Set(get().selectedSchemaTypes);
                            const schemaIdsToLoad: number[] = [];

                            set((state) => {
                                const nextTypes = new Set(schemaTypes);
                                state.selectedSchemaTypes = schemaTypes;

                                // Remove entries for deselected schema types
                                for (const key of Object.keys(state.schemaData)) {
                                    const numKey = Number(key);
                                    const entry = state.schemaData[numKey];
                                    if (entry.schema && !nextTypes.has(entry.schema.name)) {
                                        delete state.schemaData[numKey];
                                    }
                                }

                                // Add entries for newly selected schema types
                                for (const schemaType of schemaTypes) {
                                    if (prevTypes.has(schemaType)) {
                                        continue;
                                    }
                                    for (const company of state.companies) {
                                        const schema = company.schemas.find(
                                            (s) => s.name === schemaType && s.branchType === 'MAIN',
                                        );
                                        if (schema) {
                                            state.schemaData[schema.id] = {
                                                company,
                                                schema,
                                                descriptors: [],
                                                reports: [],
                                                loading: true,
                                            };
                                            schemaIdsToLoad.push(schema.id);
                                        }
                                    }
                                }
                            });

                            for (const schemaId of schemaIdsToLoad) {
                                loadReports(schemaId);
                            }
                        },

                        setCompanyData: (schemaId, data) =>
                            set((state) => {
                                if (state.schemaData[schemaId]) {
                                    state.schemaData[schemaId] = {
                                        ...state.schemaData[schemaId],
                                        ...data,
                                    };
                                }
                            }),
                    },

                    configActions: {
                        setFilterCfg: (cfg) =>
                            set((state) => {
                                state.filterCfg = cfg;
                            }),

                        setTableConfig: (cfg) =>
                            set((state) => {
                                state.tableConfig = cfg;
                            }),
                    },
                };
            }),
        ),
    );
};
